import * as d3 from 'd3';
import { EVENT_TYPES } from './types';
import type {
  Callback,
  SVGSelection,
  D3Selection,
  HotSpotSelection,
  LineSelection,
  InitialOpt
} from './types';

class SVGTool {
  svgNode: SVGSelection | null = null; // svg节点
  svgViewNode: D3Selection | null = null; // svg视口节点
  hotSpotNodes: HotSpotSelection = null; // 所有可选中节点（class为.hotspot）
  highlightPCode: string = ''; // 高亮的文本
  lineNodes: LineSelection = null; // 所有线节点
  validPCodeList: string[] = []; // 有效的pcode列表
  private handlers: {
    [key in EVENT_TYPES]?: { callback: Callback }[];
  } = {};

  constructor({ svgElement, defaultPCode, paramMaps, validPCodeList }: InitialOpt) {
    this.svgNode = d3.select(svgElement);
    this.validPCodeList = validPCodeList;
    svgElement.appendChild(this.getSvgFilter());
    this.svgViewNode = this.svgNode.select('g');
    this.hotSpotNodes = this.initializeHotSpot(paramMaps);
    this.lineNodes = this.initializeLines();
    if (defaultPCode) {
      this.hightLightNodesByPCode(defaultPCode);
    }
    this.initPosition();
    this.initEvents();
  }

  /**
   * @desc 设置线的背景渐变色
   */
  private getSvgFilter() {
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttributeNS(null, 'x', '-50%');
    filter.setAttributeNS(null, 'y', '-50%');
    filter.setAttributeNS(null, 'width', '5000%');
    filter.setAttributeNS(null, 'height', '500%');
    filter.setAttributeNS(null, 'filterUnits', 'userSpaceOnUse');
    filter.setAttributeNS(null, 'id', 'drop-shadow');

    const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
    feGaussianBlur.setAttributeNS(null, 'stdDeviation', '4');
    feGaussianBlur.setAttributeNS(null, 'in', 'SourceGraphic');
    filter.appendChild(feGaussianBlur);

    const feColorMatrix = document.createElementNS('http://www.w3.org/2000/svg', 'feColorMatrix');
    feColorMatrix.setAttributeNS(null, 'type', 'matrix');
    feColorMatrix.setAttributeNS(null, 'values', '0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 1 0');
    filter.appendChild(feColorMatrix);

    const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
    const feMergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    const feMergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    feMergeNode2.setAttributeNS(null, 'in', 'SourceGraphic');
    feMerge.appendChild(feMergeNode1);
    feMerge.appendChild(feMergeNode2);
    filter.appendChild(feMerge);
    defs.appendChild(filter);
    return defs;
  }

  private initializeLines() {
    const lines = this.svgViewNode.selectAll('line');
    const polyLines = this.svgViewNode.selectAll('polyline');
    const lineNodes = lines.nodes();
    const polyLineNodes = polyLines.nodes();
    const allNodes = lineNodes.concat(polyLineNodes);
    const allLines = d3.selectAll(allNodes);
    allLines.each((_, i, nodes) => {
      const node = nodes[i];
      const item = d3.select(node);
      item.style('cursor', 'pointer');
    });

    return allLines;
  }

  /**
   * @desc 添加title提示
   */
  private handleAddTitleTip(target: SVGGElement, textStr) {
    const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    title.textContent = textStr;
    title.classList.add('title-tip');
    target.appendChild(title);
  }

  private removeTitleTips() {
    this.svgViewNode?.selectAll('.title-tip').remove();
  }

  /**
   * @desc 拼接pcode
   * 示例
   * 1. <text><tspan>FF15</tspan><tspan>1.5A</tspan></text> --> <text pcode="FF15"><tspan>FF15</tspan><tspan>1.5A</tspan></text>
   * 2. <text><tspan>FF15</tspan><tspan>-1</tspan></text> --> <text pcode="FF15-1"><tspan>FF15</tspan><tspan>1.5A</tspan></text>
   * 3. <text><tspan>正极</tspan><tspan>保险盒</tspan></text> --> <text pcode="正极保险盒"><tspan>正极</tspan><tspan>保险盒</tspan></text>
   */
  private splicePCode(nodes) {
    let pcode = '';
    const regex = /^[a-zA-Z0-9]+$/;
    const isAlphaStart = regex.test(d3.select(nodes[0]).text());

    nodes.forEach((node, index) => {
      const item = d3.select(node);
      item.attr('fill', null); // 移除 tspan 的 fill 属性

      const text = item.text();
      if (index === 0) {
        pcode += text;
      } else {
        if (isAlphaStart) {
          if (text.startsWith('-')) {
            pcode += text;
          }
        } else {
          pcode += text;
        }
      }
    });

    return pcode;
  }

  private addHotSpot(node, pcode) {
    this.validPCodeList?.forEach(v => {
      if (pcode.indexOf(v) >= 0) {
        node.attr('pcode', pcode);
        node.style('cursor', 'pointer');
        node.classed('hotspot', true);
      }
    });
  }

  private isAllNumber(nodes) {
    const regex = /^[0-9]+$/;
    return nodes.every(node => regex.test(d3.select(node).text()));
  }

  private initializeHotSpot(paramMaps) {
    const texts = this.svgViewNode.selectAll('text');
    texts?.each((_, i, nodes) => {
      const node = nodes[i];
      const item = d3.select(node as SVGGElement);
      let pcode: string = item.text();
      let hotSpotNode = item;

      const tspanNodes = item.selectAll('tspan').nodes();
      // 1. text里有tspan
      if (tspanNodes.length > 0) pcode = this.splicePCode(tspanNodes);
      else if (node.parentNode.tagName == 'g' && !node.parentNode.id) {
        // 2. text的父节点是g
        const parentG = d3.select(node.parentNode);
        const textNodes = parentG
          .selectAll('text')
          .nodes()
          .filter(textNode => textNode.parentNode === parentG.node());
        // 2.1. 如果text有唯一的兄弟节点text **注意：只处理第一个text，避免重复**
        // 可能存在两个text都是纯数字的情况，要排除出去
        if (textNodes.length === 2 && !this.isAllNumber(textNodes)) {
          if (node === textNodes[0]) {
            pcode = this.splicePCode(textNodes);
            hotSpotNode = parentG;
          } else return;
        }

        // 2.2. 如果text有唯一的兄弟节点rect
        if (parentG.node().children.length == 2) {
          const rects = parentG.selectAll('rect');
          const rect = rects.node();
          if (rect && rect.tagName == 'rect') {
            this.addHotSpot(d3.select(rect), pcode);
          }
        }
      }

      // 添加title提示
      if (paramMaps && paramMaps[pcode])
        this.handleAddTitleTip(hotSpotNode.node(), paramMaps[pcode]);

      // 添加hotspot
      this.addHotSpot(hotSpotNode, pcode);
    });
    return this.svgViewNode.selectAll('.hotspot');
  }

  /**
   * @desc 设置热点rect的位置和大小
   */
  private initPosition() {
    this.hotSpotNodes?.each((_, i, nodes) => {
      const node = nodes[i];
      const item = d3.select(node as SVGGElement);
      const x = item.attr('x');
      if (x) return; // 已经设置过位置
      if (node.tagName != 'rect' || node.tagName != 'path') return;
      const g1 = node.parentNode;
      const box = g1.getBBox();

      item.attr('x', box.x);
      item.attr('y', box.y);
      item.attr('width', box.width);
      item.attr('height', box.height);
    });
  }

  private initEvents() {
    this.hotSpotNodes.on('click', event =>
      this.handleHotSpotClick(event.currentTarget as SVGGElement)
    );
    this.lineNodes.on('click', event => this.handleLineClick(event.currentTarget as SVGGElement));
  }

  public removeEvents() {
    this.hotSpotNodes?.on('click', null);
    this.lineNodes?.on('click', null);
  }

  private notify(type: EVENT_TYPES, data: any) {
    if (!type || !this.handlers[type]) return;
    this.handlers[type]?.forEach(({ callback }) => {
      callback(data);
    });
  }

  public subscribe(type: EVENT_TYPES, callback: Callback) {
    this.handlers[type] = this.handlers[type] || [];
    (this.handlers[type] as { callback: Callback }[]).push({ callback });
  }

  public unsubscribe(type: EVENT_TYPES) {
    this.handlers[type] = [];
  }

  public removeHandles() {
    this.handlers = {};
  }

  private removeNode() {
    this.hotSpotNodes?.remove();
    this.svgViewNode?.remove();
    this.svgNode?.remove();
    this.hotSpotNodes = null;
    this.svgNode = null;
    this.svgViewNode = null;
  }

  /**
   * @desc 销毁事件监听 && 节点
   */
  public destroy() {
    this.removeEvents();
    this.removeHandles();
    this.removeNode();
    this.removeTitleTips();
  }

  private handleHotSpotClick(target: SVGGElement) {
    const groupNode = d3.select(target);
    const pcode: string = groupNode.attr('pcode');
    // 如果已经选中||禁止选择,不再触发点击事件
    // if (this.highlightPCode === pcode) return;
    this.hightLightNodesByPCode(pcode);
    this.notify(EVENT_TYPES.TEXTCLICK, pcode); // 通知外部点击事件
  }

  /**
   *
   * @param selectTexts 选中的Text
   * @returns 选中相同Text内容的text&circle节点
   */
  public outsideHighlight(pcode: string) {
    this.hightLightNodesByPCode(pcode);
  }

  /**
   * @desc 取消高亮
   */
  private unhighlightHotSpot(node) {
    const item = d3.select(node as SVGGElement);
    if (['text', 'g', 'rect', 'path'].includes(node.tagName)) {
      item.style('fill', '');
    } else {
      item.style('fill', 'rgba(255, 255, 255, 0)'); // 透明
    }
  }

  /**
   * @desc 如果是(g|text)，fill为红色, 否则(rect)，fill为绿色；
   */
  private highlightHotSpot(node) {
    const item = d3.select(node as SVGGElement);
    if (['text', 'g'].includes(node.tagName)) {
      item.style('fill', 'rgb(255 0 0)');
    } else {
      item.style('fill', '#24cdc540');
    }
  }

  /**
   * @desc 高亮有3种情况：
   * 1. 选中pcode = 当前pcode
   * 2. 选中pcode = 当前pcode-* (FL701 | FL701-4)
   * 3. 选中pcode-* = 当前pcode (FL701-4 | FL701)
   */
  private hightLightNodesByPCode(pcode: string) {
    this.highlightPCode = pcode;
    this.hotSpotNodes?.each((_, i, nodes) => {
      const node = nodes[i];
      const item = d3.select(node as SVGGElement);
      const currentPCode = item.attr('pcode');
      this.unhighlightHotSpot(node);
      if (pcode === currentPCode) {
        this.highlightHotSpot(node);
      } else if (currentPCode.indexOf('-') >= 0) {
        const index = currentPCode.indexOf('-');
        const subPCode = currentPCode.substring(0, index);
        if (subPCode === pcode) {
          this.highlightHotSpot(node);
        }
      } else if (pcode.indexOf('-') >= 0) {
        const index = pcode.indexOf('-');
        const subPCode = pcode.substring(0, index);
        if (subPCode === currentPCode) {
          this.highlightHotSpot(node);
        }
      }
    });
  }

  private handleLineClick(target: SVGGElement) {
    const node = d3.select(target);
    // 先清空全部line的attr的filter
    this.lineNodes.each((_, i, nodes) => {
      const item = d3.select(nodes[i]);
      item.attr('filter', '');
    });
    node.attr('filter', 'url(#drop-shadow)');
  }
}

export default SVGTool;
